import { useEffect, useState } from "react";

import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";

import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import { SelectedProductFilters, ProductSearchFilters } from "../types";
import { clearedFilterValues } from "../utils";
import FilterPanel from "./FilterPanel";

type ProductFilterPanelModalProps = {
  options: ProductSearchFilters;
  isVisible: boolean;
  onClose: () => void;
  value: SelectedProductFilters;
  onChange: (search: SelectedProductFilters) => void;
};

/**
 * The filter panel in a modal, for viewports too narrow to show it beside the grid.
 *
 * Deliberately separate from `ProductSearchFilterModal` rather than a branch inside it, so the
 * V1 filter modal can stay frozen and be deleted whole when STOREFRONT_FILTERS_V2 goes.
 */
const ProductFilterPanelModal = ({
  options,
  isVisible,
  onClose,
  value,
  onChange,
}: ProductFilterPanelModalProps) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // The params have to be current when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetSearch = () =>
    setUpdatedFilters({
      ...updatedFilters,
      ...clearedFilterValues(PRODUCT_FILTER_DEFINITIONS),
    });

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  // Every filter counts, including any whose options came back empty: Reset is the only way to
  // clear a value that has no control on screen.
  const selectedCount = PRODUCT_FILTER_DEFINITIONS.reduce(
    (total, definition) => total + definition.selectedFrom(updatedFilters).length,
    0,
  );

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <FilterPanel<SelectedProductFilters, ProductSearchFilters>
            definitions={PRODUCT_FILTER_DEFINITIONS}
            filterOptions={options}
            filters={updatedFilters}
            onChange={(next) => setUpdatedFilters({ ...updatedFilters, ...next })}
            hasSelectedFilters={!!selectedCount}
            onClearAll={resetSearch}
            showHeader={false}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetSearch}
            disabled={!selectedCount}
          >
            Reset {toCount(selectedCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default ProductFilterPanelModal;
